import React, { useState, useEffect } from 'react';
import { Participant } from '../features/expenseSplitter/groupModel';
import { useGroup } from '../features/expenseSplitter/groupContext';
import { navigateToLanding } from '../services/router';
import ExpenseForm from '../components/ExpenseForm';
import ExpenseList from '../components/ExpenseList';
import { CreateExpenseParams } from '../features/expenseSplitter/expenseService';
import { generateShareUrl } from '../features/expenseSplitter/shareLink';

/**
 * Group Page
 * 
 * Main expense splitting interface - the core of the application.
 * Handles:
 * - Group management (name, participants)
 * - Expense entry (amount, payer, split modes)
 * - Live balance calculations
 * - Settlement suggestions
 * - Real-time collaboration via shared links
 * - Expense history display
 * 
 * User Journey: Main workspace after group creation or link access
 * Key Features: Add expense, balances view, settle suggestions, share link
 */

interface GroupPageProps {
  groupId?: string;
}

const Group: React.FC<GroupPageProps> = ({ groupId }) => {
  const { state, actions } = useGroup();
  const [newParticipantName, setNewParticipantName] = useState('');
  const [isAddingParticipant, setIsAddingParticipant] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [isAddingExpense, setIsAddingExpense] = useState(false);

  // Load group data when groupId changes
  useEffect(() => {
    if (!groupId) {
      return;
    }

    actions.loadGroup(groupId);
  }, [groupId, actions]);

  const handleAddParticipant = async () => {
    if (!state.currentGroup || !newParticipantName.trim()) return;

    setIsAddingParticipant(true);
    try {
      await actions.addParticipant({
        name: newParticipantName.trim()
      });
      setNewParticipantName('');
    } catch (error) {
      // Error is handled by context and displayed via state.error
      console.error('Failed to add participant:', error);
    } finally {
      setIsAddingParticipant(false);
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!state.currentGroup) return;

    if (confirm('Are you sure you want to remove this participant?')) {
      try {
        await actions.removeParticipant(participantId);
      } catch (error) {
        // Error is handled by context and displayed via state.error
        console.error('Failed to remove participant:', error);
      }
    }
  };

  const handleBackToHome = () => {
    actions.clearGroup();
    navigateToLanding();
  };

  const handleClearError = () => {
    actions.clearError();
  };

  const handleShowExpenseForm = () => {
    setShowExpenseForm(true);
  };

  const handleHideExpenseForm = () => {
    setShowExpenseForm(false);
  };

  const handleAddExpense = async (params: CreateExpenseParams) => {
    setIsAddingExpense(true);
    try {
      await actions.addExpense(params);
      setShowExpenseForm(false);
    } catch (error) {
      // Error is handled by context and displayed via state.error
      console.error('Failed to add expense:', error);
    } finally {
      setIsAddingExpense(false);
    }
  };

  if (state.loading) {
    return (
      <div className="group-page content" style={{ maxWidth: '800px', margin: '0 auto', marginTop: '60px' }}>
        <p>Loading group...</p>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="group-page content" style={{ maxWidth: '800px', margin: '0 auto', marginTop: '60px' }}>
        <h1>Error</h1>
        <p>{state.error}</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button className="btn btn-secondary" onClick={handleBackToHome}>
            Back to Home
          </button>
          <button className="btn btn-primary" onClick={handleClearError}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!state.currentGroup) {
    return (
      <div className="group-page content" style={{ maxWidth: '800px', margin: '0 auto', marginTop: '60px' }}>
        <p>No group loaded</p>
        <button className="btn btn-secondary" onClick={handleBackToHome}>
          Back to Home
        </button>
      </div>
    );
  }

  const group = state.currentGroup;

  return (
    <div className="group-page content" style={{ maxWidth: '800px', margin: '0 auto', marginTop: '60px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1>{group.name}</h1>
          <p style={{ color: 'var(--color-gray-600)', margin: '4px 0 0 0' }}>
            Created {new Date(group.createdAt).toLocaleDateString()}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={handleBackToHome}>
          ← Back to Home
        </button>
      </div>

      {/* Participants Section */}
      <div style={{ marginBottom: '40px' }}>
        <h3 style={{ marginBottom: '16px' }}>Participants ({group.participants.length})</h3>
        
        {/* Participant List */}
        <div style={{ marginBottom: '20px' }}>
          {group.participants.map((participant: Participant) => (
            <div 
              key={participant.id} 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid var(--color-gray-100)'
              }}
            >
              <div>
                <strong>{participant.name}</strong>
                {participant.email && (
                  <span style={{ color: 'var(--color-gray-600)', marginLeft: '8px' }}>
                    {participant.email}
                  </span>
                )}
              </div>
              {group.participants.length > 1 && (
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleRemoveParticipant(participant.id)}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add Participant */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Add participant name..."
            value={newParticipantName}
            onChange={(e) => setNewParticipantName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddParticipant()}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: '1px solid var(--color-gray-200)',
              borderRadius: 'var(--radius-base)',
              fontSize: '14px'
            }}
          />
          <button 
            className="btn btn-primary"
            onClick={handleAddParticipant}
            disabled={!newParticipantName.trim() || isAddingParticipant}
          >
            {isAddingParticipant ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>

      {/* Expenses Section */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3>Expenses ({group.expenses.length})</h3>
          <button 
            className="btn btn-success" 
            onClick={handleShowExpenseForm}
            disabled={group.participants.length < 1}
          >
            Add Expense
          </button>
        </div>

        <ExpenseList
          expenses={group.expenses}
          participants={group.participants}
          balances={group.balances || {}}
          currency={group.defaultCurrency}
        />
      </div>

      {/* Share Section */}
      <div style={{ 
        padding: '20px', 
        backgroundColor: 'var(--color-gray-50)', 
        borderRadius: 'var(--radius-lg)',
        marginTop: '32px'
      }}>
        <h4 style={{ marginBottom: '12px' }}>Share this group</h4>
        <p style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '12px' }}>
          Others can join and add expenses using this link:
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={generateShareUrl(group)}
            readOnly
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid var(--color-gray-200)',
              borderRadius: 'var(--radius-base)',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          />
          <button 
            className="btn btn-secondary"
            onClick={() => navigator.clipboard.writeText(generateShareUrl(group))}
          >
            Copy
          </button>
        </div>
      </div>

      {/* Expense Form Modal */}
      {showExpenseForm && (
        <ExpenseForm
          participants={group.participants}
          defaultCurrency={group.defaultCurrency}
          onSubmit={handleAddExpense}
          onCancel={handleHideExpenseForm}
          isSubmitting={isAddingExpense}
        />
      )}
    </div>
  );
};

export default Group;
